import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Globe2, Sparkles } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import usePageMeta from "../hooks/usePageMeta";

const STATS = [
  { label: "Cover starts at", value: "KES 99/wk" },
  { label: "FlashClaim median", value: "< 90s" },
  { label: "Built for", value: "African merchants" },
];

export default function About() {
  usePageMeta({
    title: "About Fortiqa — Cyber Cover. Business Confidence.",
    description:
      "Fortiqa builds embedded cyber micro-insurance and merchant protection for African businesses. IRA-aligned, WhatsApp-first.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="about-page">
      <header className="px-6 lg:px-10 max-w-7xl mx-auto pt-20 pb-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#5c6360] hover:text-[#114232] mb-10"
          data-testid="about-back"
        >
          ← Back
        </Link>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337] mb-3">
          About Fortiqa
        </div>
        <h1 className="font-heading text-5xl lg:text-6xl font-extrabold leading-[0.95] text-[#114232] max-w-3xl">
          Cyber cover, built for the merchants powering African commerce.
        </h1>
        <p className="text-lg text-[#5c6360] max-w-2xl mt-6 leading-relaxed">
          We build embedded micro-insurance for the next billion mobile-money
          merchants. From the Nairobi mama-mboga to the Lagos social-commerce
          seller — when something goes wrong, FlashClaim pays them in seconds.
        </p>
      </header>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">
                {s.label}
              </div>
              <div className="font-heading text-2xl font-bold text-[#114232] mt-2">
                {s.value}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-20 grid lg:grid-cols-3 gap-6">
        {[
          {
            icon: ShieldCheck,
            title: "Protection-first product",
            body: "Cyber loss happens to the smallest businesses first. We meet them where they trade — WhatsApp, M-Pesa, POS — with cover that activates in 60 seconds.",
          },
          {
            icon: Globe2,
            title: "Pan-African by design",
            body: "Kenya today, Nigeria + Ghana + Egypt next. One platform, partner-bank distribution, local underwriter partnerships, and shared fraud intelligence.",
          },
          {
            icon: Sparkles,
            title: "AI-augmented, human-reviewed",
            body: "Every claim runs through our CyberScore + OCR pipeline; high-trust claims auto-approve, edge cases route to our SIU team within minutes.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="rounded-2xl bg-white border border-[#114232]/10 p-7"
          >
            <div className="w-10 h-10 rounded-xl bg-[#114232]/8 grid place-items-center mb-4">
              <Icon className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
            </div>
            <div className="font-heading text-lg font-bold text-[#114232] mb-2">
              {title}
            </div>
            <p className="text-sm text-[#5c6360] leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
        <div className="bg-[#114232] rounded-3xl p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
              Want to partner?
            </div>
            <h3 className="font-heading text-3xl font-bold leading-tight">
              We work with banks, fintechs, and merchant networks.
            </h3>
          </div>
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-colors"
            data-testid="about-cta-partners"
          >
            See partner program <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
